/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import isEqual from 'lodash.isequal';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import EnvironmentModel from 'cio/models/environment';
import { IdentifierConfig, SelfServeMigrateTo } from 'cio/utils/identifiers';
import AnalyticsService from 'cio/services/analytics';
import ApiService from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';
import { AutoMergeOption } from 'cio/utils/components/self-serve-migration-modal';

interface SelfServeMigrationModalArgs {
  environment: EnvironmentModel;
  email: string;
  migrateTo: SelfServeMigrateTo;
  readOnly: boolean;
  onClose: () => unknown;
  onMigrationStarted: () => unknown;
  activeModal: SelfServeMigrateTo | null;
}

enum MigrationStep {
  Setup = 'setup',
  Review = 'review',
  Success = 'success',
}

export default class SelfServeMigrationModal extends Component<SelfServeMigrationModalArgs> {
  @service analytics!: AnalyticsService;
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;

  readonly steps = MigrationStep;
  readonly originalIdentifiers;
  readonly SelfServeMigrateTo = SelfServeMigrateTo;

  @tracked haveIdentifiersChanged = false;
  @tracked currentStep = MigrationStep.Setup;
  @tracked selectedOption: AutoMergeOption | null = null;

  constructor(owner: unknown, args: SelfServeMigrationModalArgs) {
    super(owner, args);

    // This is for tracking identifier updates if the conversion finishes before
    // the modal is closed. If that happens, we show a success message in the modal.
    this.originalIdentifiers = args.environment.identifiers;

    if (this.args.migrateTo === SelfServeMigrateTo.IdOnly) {
      this.currentStep = MigrationStep.Review;
    }
  }

  get title() {
    if (this.currentStep === MigrationStep.Success) {
      return "We've started the configuration change!";
    } else if (this.args.migrateTo === SelfServeMigrateTo.MultiId) {
      return 'Enable email or id to identify people';
    } else {
      return 'Disable email to identify people';
    }
  }

  beginMigration = task(async () => {
    try {
      let data: Record<string, string> = { migrate_to: this.args.migrateTo };

      if (this.args.migrateTo === SelfServeMigrateTo.MultiId) {
        data.merge_mode = this.selectedOption ?? AutoMergeOption.Manual;
      }

      await this.api.post('migrate', { data });

      this.currentStep = MigrationStep.Success;
      this.analytics.track('workspace', 'identifier_change_submitted', {
        identifiers: this.args.environment.identifiers,
        migrateTo: this.args.migrateTo,
      });
      this.args.onMigrationStarted();
      return true;
    } catch (err) {
      this.apiErrors.report(err);

      return false;
    }
  });

  @action
  trackOpen() {
    this.analytics.track('workspace', 'identifier_change_started', { identifiers: this.args.environment.identifiers });
  }

  @action
  setIdentifiersHaveChanged(_: Element, [identifiers]: [IdentifierConfig]) {
    if (!isEqual(this.originalIdentifiers, identifiers)) {
      this.haveIdentifiersChanged = true;
    }
  }

  @action
  changeStep(step: MigrationStep) {
    this.currentStep = step;
  }

  @action
  handleMergeOptionSelect(value: AutoMergeOption) {
    this.selectedOption = value;
  }
}
